/**
 * Per-arm content attributes — maps between the wizard's per-arm accordion
 * selections and the experiment wire fields `armContentAttributes` (keyed by
 * enriched arm letter) + `contentAttributes` (the union, kept as the fallback
 * for any arm without an entry). See `docs/experiment-per-arm-content-attributes.md`.
 */

import type {
  ArmCombination,
  VariantArmId,
} from '~/features/configuration/types/treatment-configuration.types';
import { variantArmsForCombination } from '~/features/configuration/utils/arm-combinations';

import type { ContentAttribute, ContentAttributeSelection } from '../types/create-experiment.types';
import type { ArmContentAttributes } from '../types/experiment-draft.types';

/** Every enriched arm, in display order. The store keeps a selection for each. */
export const ENRICHED_ARMS: readonly VariantArmId[] = ['B', 'C'];

/** Per-arm accordion selections as held in the wizard store. */
export type ArmSelections = Partial<Record<VariantArmId, ContentAttributeSelection>>;

/**
 * Flatten one accordion selection to the agent-services catalog KEYS the backend
 * expects (e.g. `title`, `product_highlight`) — sending labels silently dropped
 * the selection downstream. Sub-ids missing from the catalog are skipped.
 */
export function selectionToKeys(
  selection: ContentAttributeSelection | undefined,
  catalog: readonly ContentAttribute[],
): string[] {
  const subKeyById = new Map<string, string>();
  for (const attr of catalog) {
    for (const sub of attr.subAttributes) subKeyById.set(sub.id, sub.key);
  }
  const keys: string[] = [];
  for (const subs of Object.values(selection ?? {})) {
    for (const [subId, selected] of Object.entries(subs)) {
      if (!selected) continue;
      const key = subKeyById.get(subId);
      if (key) keys.push(key);
    }
  }
  return keys;
}

/** Whether an accordion selection has at least one sub-attribute ticked. */
export function hasAnySelected(selection: ContentAttributeSelection | undefined): boolean {
  return Object.values(selection ?? {}).some((section) => Object.values(section).some(Boolean));
}

/**
 * Enriched arms in the combination with nothing selected. The backend rejects
 * an empty per-arm list (400), so the Strategy step blocks Next on these.
 */
export function armsMissingContentAttributes(
  combination: ArmCombination | null,
  byArm: ArmSelections,
): VariantArmId[] {
  if (combination === null) return [];
  return variantArmsForCombination(combination).filter((arm) => !hasAnySelected(byArm[arm]));
}

/**
 * Build the PATCH fields for the arms the combination runs: one key list per
 * enriched arm, plus their de-duplicated union as `contentAttributes` (the
 * fallback older screens and arm-less paths still read). Arms outside the
 * combination are never sent — the backend 400s on them.
 */
export function buildArmContentAttributesPayload(
  combination: ArmCombination,
  byArm: ArmSelections,
  catalog: readonly ContentAttribute[],
): { armContentAttributes: ArmContentAttributes; contentAttributes: string[] } {
  const armContentAttributes: ArmContentAttributes = {};
  const union = new Set<string>();
  for (const arm of variantArmsForCombination(combination)) {
    const keys = selectionToKeys(byArm[arm], catalog);
    armContentAttributes[arm] = keys;
    for (const key of keys) union.add(key);
  }
  return { armContentAttributes, contentAttributes: [...union] };
}

/**
 * Wire `string[]` of catalog keys → nested `Record<attrId, Record<subId, boolean>>`.
 *
 *   - `null`/`undefined` — never set: returns `{}` so the Strategy step's init
 *     effect seeds the defaults (Discovery & Click-Through on, the rest off).
 *   - `[]` — user explicitly deselected everything: a fully-populated map of
 *     `false`, so the "skip when populated" init guard preserves their intent.
 *   - `[key, …]` — partial selection, matched by key.
 *
 * Catalog absent → `{}`; the Strategy step re-initialises once it lands.
 */
export function inflateContentAttributes(
  selected: readonly string[] | null | undefined,
  catalog: readonly ContentAttribute[] | undefined,
): ContentAttributeSelection {
  if (!catalog) return {};
  if (selected === null || selected === undefined) return {};
  const selectedKeys = new Set(selected);
  const out: ContentAttributeSelection = {};
  for (const attr of catalog) {
    const section: Record<string, boolean> = {};
    for (const sub of attr.subAttributes) {
      section[sub.id] = selectedKeys.has(sub.key);
    }
    out[attr.id] = section;
  }
  return out;
}

/**
 * Hydrate every enriched arm's selection from a saved draft. An arm missing from
 * `armContentAttributes` (or a `null` map — a draft that predates per-arm
 * selection) falls back to the single legacy `contentAttributes`. Arms that
 * inflate to nothing are omitted so the init effect seeds their defaults.
 */
export function hydrateArmSelections(
  dto: {
    armContentAttributes?: ArmContentAttributes | null;
    contentAttributes?: readonly string[] | null;
  },
  catalog: readonly ContentAttribute[] | undefined,
): ArmSelections {
  const out: ArmSelections = {};
  for (const arm of ENRICHED_ARMS) {
    const inflated = inflateContentAttributes(
      dto.armContentAttributes?.[arm] ?? dto.contentAttributes,
      catalog,
    );
    if (Object.keys(inflated).length > 0) out[arm] = inflated;
  }
  return out;
}
